/**
 * Chat context chip — small inline lilac pill that names the entity / page
 * the current chat is bound to. Rendered inside the chat content flow
 * (above the welcome message on an empty thread, and as a self-contained
 * affordance the welcome screen owns), NOT as a sticky/static row above
 * the thread. Sized and styled like the welcome / follow-up suggestion
 * chips so it reads as part of the same chip family.
 *
 * Shape (PLT-4929):
 *
 *   ┌────────────────────────────────────────────────────────────────────┐
 *   │ [U] Double materiality · Acme · Climate change mitigation (Tail…)  ×│
 *   └────────────────────────────────────────────────────────────────────┘
 *
 * The chip mirrors the page breadcrumb — `{lens} · {entity} · {detail}` —
 * rather than the older "Discussing {entity}" framing. The entity name is
 * still the visually-prominent token; the lens and detail flank it as
 * lighter-weight breadcrumb segments. When no lens / detail is published
 * (plain entity page), the chip collapses to just the entity name.
 *
 * Layout:
 *   - Favicon (Upright glyph) on the left.
 *   - Breadcrumb sentence in the middle — a single click target (`<a>`)
 *     that lands on the bound page.
 *   - Dismiss button (`×`) on the right — separate click target. Clicking
 *     it drops the live page-context sources (sticky binding, sticky path
 *     context, `pageChatContext`, and `hiddenEntityContext`) so the chip
 *     disappears and the next message is answered without the page
 *     context — but the conversation itself is preserved. There is no
 *     "no context" indicator after dismissal; the row simply collapses.
 *
 * Persistence across navigation (PLT-4701 follow-up): the live binding /
 * path-context publishers unmount when the user expands the chat side
 * panel to full `/chat`, which previously caused the chip to disappear
 * mid-conversation. The chip reads from the *sticky* sibling stores
 * (`useStickyPageChatBinding` / `useStickyPageChatPathContext`) which
 * mirror the most-recent non-null value for the lifetime of the
 * conversation, cleared explicitly on `New chat` or on `×` dismissal.
 *
 * Label composition:
 *
 *   - Preferred: build the sentence directly from the sticky binding
 *     (`entity name`) plus the sticky path context (`lensLabel`,
 *     `detailLabel`, `variantLabel`). This yields the consistent
 *     breadcrumb form regardless of which surface opened the chat.
 *   - Fallback: when no binding is available (rare — e.g. chat opened
 *     from a non-entity surface that still published a `pageChatContext`),
 *     fall back to the raw `pageChatContext.label` text.
 *
 * Link target: `pageChatContext.href` wins when set (gives us the exact
 * lens / matter / variant URL "for free"); else the binding's default
 * platform URL (`getCompanyViewUrl` etc.).
 *
 * DMA-Freemium own-company special case: when the bound entity is the
 * Freemium org's own company, the chip reads "{company name} (your company)"
 * and links to `/dashboard` — same rule as `buildCompanyPageUrl(user)`
 * server-side. No breadcrumb decorator is applied in this case.
 *
 * Data sources:
 *   - `pageChatContext` (`upright-chat-shared.ts`): { label, href }, set by
 *     side-panel openers — gives us the exact URL the user is looking at.
 *   - `useStickyPageChatBinding` (`current-page-chat-binding.ts`):
 *     last-seen { entityId, entityType, label }, retained across publisher
 *     unmount.
 *   - `useStickyPageChatPathContext` (`current-page-chat-path-context.ts`):
 *     last-seen { lensLabel?, detailLabel?, variantLabel? } from sub-route
 *     publishers, retained across publisher unmount.
 */
import { type ReactNode, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { ModelEntityType } from '@upright/common/model/input/types';
import { UprightLogoSymbolBlackWhite } from '@upright/ui/common/generic/components/icons/logo/upright-logo-symbol-black-white';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { getOrganizationLinkedCompanyId } from '@upright/common/iam/organizations/common';
import { isDmaFreemiumUser } from '@upright/common/utils';
import { getCompanyGroupViewUrl, getCompanyViewUrl, getPortfolioViewUrl } from '@upright/ui/untidy/urls';
import {
  type CurrentPageChatBinding,
  resetStickyPageChatBinding,
  useStickyPageChatBinding,
} from './current-page-chat-binding';
import {
  type CurrentPageChatPathContext,
  resetStickyPageChatPathContext,
  useStickyPageChatPathContext,
} from './current-page-chat-path-context';
import {
  getPageChatContext,
  hiddenEntityContext,
  setPageChatContext,
  subscribePageChatContext,
  type PageChatContext,
} from './upright-chat-shared';
import { InlineLockBadge } from './upright-chat-tool-uis';
import { getCompanyVisibility, subscribeVisibilityRegistry, type RegisteredVisibility } from './visibility-registry';

interface ResolvedContext {
  /** Entity name to render as the visually-prominent token. `null` when only
   *  a `pageChatContext.label` is available (no binding), in which case the
   *  whole sentence is rendered from `fallbackLabel` instead. */
  entityName: string | null;
  /** Plain label used when `entityName` is `null` — falls back to the raw
   *  `pageChatContext.label` text (e.g. `"Acme — Net impact"`). */
  fallbackLabel: string | null;
  /** Where the link sub-element navigates to. Never `null` — every resolved
   *  card resolves to a single destination. */
  href: string;
  /** True only for the DMA-Freemium "viewing my own company" case. Drives the
   *  suppression of the private pill and the breadcrumb decorator — these are
   *  keyed off this flag rather than the rendered label, because the label now
   *  carries the company name (e.g. `"Acme Corp (your company)"`) and can no longer
   *  be string-matched. */
  isOwnCompany: boolean;
}

// `CurrentPageChatBinding.entityType` is typed as plain `string` (the
// interface leaves room for non-enum values such as DD-draft entity types).
// Cast each enum member to `string` once so the switch can compare against
// the field without the unsafe-enum-comparison lint complaining about a
// missing shared enum type.
const ENTITY_TYPE_COMPANY: string = ModelEntityType.COMPANY;
const ENTITY_TYPE_GROUP: string = ModelEntityType.GROUP;
const ENTITY_TYPE_PORTFOLIO: string = ModelEntityType.PORTFOLIO;

/**
 * Resolve the entity's default Platform page from a `CurrentPageChatBinding`.
 *
 * Mirrors the URL builders the platform's own page-routing already uses, so
 * the card lands on the same page the entity's name would link to anywhere
 * else in the product. Returns `null` only for entity types we don't render
 * cards for (products / mixins / phenomena — these aren't published by
 * `usePublishCurrentPageChatBinding` today, but the type system lets the
 * field through, so we degrade gracefully rather than throwing).
 */
function defaultUrlForBinding(binding: CurrentPageChatBinding): string | null {
  switch (binding.entityType) {
    case ENTITY_TYPE_COMPANY:
      return getCompanyViewUrl(binding.entityId, binding.label);
    case ENTITY_TYPE_GROUP:
      return getCompanyGroupViewUrl(binding.entityId, binding.label);
    case ENTITY_TYPE_PORTFOLIO:
      return getPortfolioViewUrl(binding.entityId, binding.label);
    default:
      return null;
  }
}

/**
 * Merge `pageChatContext` + `CurrentPageChatBinding` + the logged-in user
 * into the single card display contract. Order of preference:
 *
 *   0. A supplier's page context → render it verbatim, whatever the page's
 *      binding says. The one case where page context outranks a binding.
 *   1. DMA-Freemium "viewing my own company" → entity name reads as
 *      `"{company name} (your company)"`, link to `/dashboard`. This is
 *      the same home-page rule `buildCompanyPageUrl(user)` uses server-side.
 *   2. `binding` (preferred when present) — `entityName` is the binding
 *      label; `href` is `pageChatContext.href` when set (richest URL),
 *      else the binding's default Platform URL.
 *   3. `pageChatContext`-only fallback — no binding to anchor the
 *      breadcrumb on; render `pageCtx.label` verbatim as the whole
 *      sentence.
 *
 * Returns `null` only when nothing useful is bound (plain `/chat` cold
 * start with no entity view ever mounted).
 */
function resolveContext(
  pageCtx: PageChatContext | null,
  binding: CurrentPageChatBinding | null,
  freemiumOwnCompanyId: string | undefined,
): ResolvedContext | null {
  // Rule 0 — a supplier's page context beats every binding on the page
  // (PLT-6269). The suppliers list lives on the buyer's own company page — on
  // DMA Freemium, on the *user's* own company page — so the binding here is the
  // buyer, and rule 1 would go further and decorate it "(your company)". Either
  // one puts the buyer's name on a thread that is about one of its suppliers.
  // A supplier reaches the chat as page context and nothing else
  // (`supplier-results-panel.tsx`'s "Continue in chat"), so that context is the
  // specific signal and it wins outright.
  //
  // Rendered through the label rather than as a structured `entityName`, which
  // drops the breadcrumb and the private pill, and both are right to drop: a
  // supplier has no page of its own for the name to link to, the lens
  // breadcrumb on this page is the buyer's ("Double materiality"), and the
  // visibility the pill would read is the buyer's too. The label already reads
  // "{supplier} — Supplier risk" and its href returns to the list the reader
  // opened it from. The welcome screen keys its suggestion chips off this same
  // `entityType === 'supplier'` signal, so the chip and the chips cannot end up
  // naming two different companies.
  if (pageCtx?.entityType === 'supplier') {
    return { entityName: null, fallbackLabel: pageCtx.label, href: pageCtx.href, isOwnCompany: false };
  }

  // Rule 1 — DMA-Freemium own-company shortcut. Has to land before the
  // pageChatContext branch — otherwise a deep-link from /company/{theirOwnId}/dma
  // would resolve via the generic binding branch and link to the company page
  // instead of the dashboard. The chip shows the company name with a "your
  // company" qualifier (e.g. "Acme Corp (your company)") and still links to
  // `/dashboard` — the same home-page rule `buildCompanyPageUrl(user)` uses
  // server-side. `isOwnCompany` (not the label text) drives the private-pill
  // and breadcrumb suppression downstream.
  if (
    freemiumOwnCompanyId &&
    binding?.entityType === ENTITY_TYPE_COMPANY &&
    binding.entityId === freemiumOwnCompanyId
  ) {
    return {
      entityName: `${binding.label} (your company)`,
      fallbackLabel: null,
      href: '/dashboard',
      isOwnCompany: true,
    };
  }

  // Rule 2 — binding gives us the entity name as a structured token,
  // which lets us compose the breadcrumb sentence rather than relying on
  // pre-baked label strings. URL still prefers `pageChatContext.href`
  // when set (it encodes the exact lens / matter the user is looking at).
  if (binding) {
    const href = pageCtx?.href ?? defaultUrlForBinding(binding);
    if (href !== null) {
      return { entityName: binding.label, fallbackLabel: null, href, isOwnCompany: false };
    }
  }

  // Rule 3 — pageChatContext-only fallback. No binding means we can't
  // compose a structured breadcrumb; render the raw label as-is.
  if (pageCtx) {
    return { entityName: null, fallbackLabel: pageCtx.label, href: pageCtx.href, isOwnCompany: false };
  }

  return null;
}

/**
 * Reactive lookup for the bound company's visibility, if any.
 *
 * Returns `undefined` until an MCP tool result has registered visibility for
 * the bound company id — at which point the card re-renders and the private
 * pill (when applicable) fades in. We only consult the registry for
 * `entityType === 'company'`: groups / portfolios don't carry an
 * organisation-scoped privacy bit today, and pageChatContext-only cards
 * have no entity id to look up.
 */
function useBoundCompanyVisibility(binding: CurrentPageChatBinding | null): RegisteredVisibility | undefined {
  // Subscribe to the registry so a fresh tool result triggers a re-render
  // without needing to remount the chat. Snapshot returns whatever the
  // synchronous lookup currently sees — if no entry is registered yet, we
  // simply render the card without the pill.
  const lookup = (): RegisteredVisibility | undefined => {
    if (!binding || binding.entityType !== ENTITY_TYPE_COMPANY) return undefined;
    return getCompanyVisibility(binding.entityId);
  };
  return useSyncExternalStore(subscribeVisibilityRegistry, lookup, () => undefined);
}

/**
 * Render the leading lens breadcrumb segment — the `{lensLabel} · ` prefix
 * that sits before the entity name. Returns `null` when no `lensLabel` is
 * published, in which case the chip starts directly with the entity name
 * (plain entity page, e.g. company landing page with no sub-route).
 */
function renderLensPrefix(pathContext: CurrentPageChatPathContext | null): ReactNode {
  if (pathContext === null || !pathContext.lensLabel) return null;
  return (
    <>
      <span className="upright-chat-context-chip-lens">{pathContext.lensLabel}</span>
      <span className="upright-chat-context-chip-sep" aria-hidden="true">
        {' · '}
      </span>
    </>
  );
}

/**
 * Render the trailing breadcrumb suffix — the `· {detailLabel} ({variantLabel})`
 * fragment after the entity name. Conventions:
 *
 *   - Detail only meaningful when a `lensLabel` is also set (otherwise the
 *     sentence has no lens to drill down inside). Defends against publisher
 *     mistakes by ignoring orphan details.
 *   - Variant is always allowed — it qualifies the entity itself, not the
 *     lens, so `Acme (Pre-release)` is a valid sentence even without a lens.
 *
 *   - No detail, no variant → returns `null`.
 *   - Detail only → ` · {detailLabel}`.
 *   - Variant only → ` ({variantLabel})`.
 *   - Both → ` · {detailLabel} ({variantLabel})`.
 */
function renderTrailingDetail(pathContext: CurrentPageChatPathContext | null): ReactNode {
  if (pathContext === null) return null;
  const { lensLabel, detailLabel, variantLabel } = pathContext;
  const hasDetail = lensLabel !== undefined && detailLabel !== undefined;
  if (!hasDetail && !variantLabel) return null;
  return (
    <>
      {hasDetail && (
        <>
          <span className="upright-chat-context-chip-sep" aria-hidden="true">
            {' · '}
          </span>
          <span className="upright-chat-context-chip-detail">{detailLabel}</span>
        </>
      )}
      {variantLabel && (
        <>
          {' '}
          <span className="upright-chat-context-chip-variant">{`(${variantLabel})`}</span>
        </>
      )}
    </>
  );
}

/**
 * Clear all page-context sources backing the chip — sticky binding,
 * sticky path context, the visible `pageChatContext`, and the hidden
 * entity context sent to the model. The conversation itself is NOT
 * reset; this is strictly a "remove the page-of-origin context" action
 * (PLT-4929). After this fires, `ChatContextCard` resolves to `null` on
 * the next render and the chip disappears — there is no "no context"
 * placeholder by design.
 *
 * Important: do NOT call `resetChatLoggingState()` — that would also
 * reset the thread id, drop the analytics tracking, and reset state the
 * user did not ask to drop. The X button is specifically the *narrower*
 * action; users who want a full reset have "New chat" elsewhere in the
 * surface.
 */
function dismissPageContext(): void {
  resetStickyPageChatBinding();
  resetStickyPageChatPathContext();
  setPageChatContext(null);
  hiddenEntityContext.value = null;
}

export function ChatContextCard() {
  // Subscribe to `pageChatContext` reactively — same pattern the welcome
  // screen uses. Server-render gets `null`.
  const pageCtx = useSyncExternalStore(subscribePageChatContext, getPageChatContext, () => null);
  // Sticky variants of the binding / path-context stores — see the file
  // header for why. The chip survives publisher unmount (i.e. expanding
  // the side panel to full `/chat`, where the entity view that publishes
  // the binding unmounts) and is cleared explicitly on `New chat` or on
  // `×` dismissal.
  const binding = useStickyPageChatBinding();
  const pathContext = useStickyPageChatPathContext();
  const user = useGetLoggedInUser();
  const freemiumOwnCompanyId = isDmaFreemiumUser(user) ? getOrganizationLinkedCompanyId(user!) : undefined;
  const visibility = useBoundCompanyVisibility(binding);

  const resolved = resolveContext(pageCtx, binding, freemiumOwnCompanyId);

  // Empty state — no chip. Header geometry is allowed to shift. The
  // wireframe explicitly rejected an empty "New chat" placeholder card.
  if (resolved === null) return null;

  // Private pill only on a structured-entity card with a real binding —
  // not on the Freemium own-company framing (the company IS your
  // org's own, so "[Private]" reads awkwardly), and not on the
  // pageChatContext-only fallback (no entity id to consult).
  const showPrivatePill =
    visibility?.visibility === 'private' && !resolved.isOwnCompany && resolved.entityName !== null;

  // Breadcrumb decorator (lens prefix + trailing detail / variant) is
  // suppressed on the Freemium own-company row — the home-page
  // reading reads as a friendly summary, not a deep-link descriptor.
  // Also suppressed on the pageChatContext-only fallback because we
  // don't have an anchor entity name to wrap with.
  const showBreadcrumb = !resolved.isOwnCompany && resolved.entityName !== null;
  const lensPrefix = showBreadcrumb ? renderLensPrefix(pathContext) : null;
  const trailingDetail = showBreadcrumb ? renderTrailingDetail(pathContext) : null;

  // Compose a precise `title` so the native hover tooltip reads like the
  // visible sentence — useful when the chip ellipsis-clips a long line.
  const linkTitle = (() => {
    if (resolved.entityName === null) {
      return `Open ${resolved.fallbackLabel ?? 'page'}`;
    }
    if (!showBreadcrumb || !pathContext?.lensLabel) return `Open ${resolved.entityName}`;
    const parts: string[] = [pathContext.lensLabel, resolved.entityName];
    if (pathContext.detailLabel) parts.push(pathContext.detailLabel);
    if (pathContext.variantLabel) parts.push(`(${pathContext.variantLabel})`);
    return `Open ${parts.join(' — ')}`;
  })();

  // Layout: the chip is a flex container holding (a) a link sub-element
  // for favicon + sentence and (b) a dismiss button. The link carries
  // the hover/focus surface effect; the dismiss button has its own
  // smaller affordance and is a sibling rather than a child so a click
  // on it does not navigate as the link.
  return (
    <span className="upright-chat-context-chip">
      <ChipLink href={resolved.href} title={linkTitle}>
        <span className="upright-chat-context-chip-mark" aria-hidden="true">
          <UprightLogoSymbolBlackWhite />
        </span>
        <span className="upright-chat-context-chip-sentence">
          {resolved.entityName === null ? (
            // pageChatContext-only fallback — no breadcrumb composition,
            // just the pre-baked label.
            <span className="upright-chat-context-chip-name">{resolved.fallbackLabel}</span>
          ) : (
            <>
              {lensPrefix}
              <span className="upright-chat-context-chip-name">{resolved.entityName}</span>
              {showPrivatePill && (
                <>
                  {' '}
                  <InlineLockBadge />
                </>
              )}
              {trailingDetail}
            </>
          )}
        </span>
      </ChipLink>
      <button
        type="button"
        className="upright-chat-context-chip-dismiss"
        onClick={dismissPageContext}
        aria-label="Remove page context"
        title="Remove page context"
      >
        {/* Inline X glyph — kept inline rather than added to the shared
            icon set, because the chip is the only consumer and the SVG
            is two lines. `currentColor` lets the surrounding button
            drive the stroke colour via CSS (lilac variants on
            hover/focus). */}
        <svg
          className="upright-chat-context-chip-dismiss-glyph"
          viewBox="0 0 12 12"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M3 3 L9 9 M9 3 L3 9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
    </span>
  );
}

/**
 * The chip's link to the page. An in-app path is a router navigation rather than a page reload,
 * so the page it opens records the chat as where it came from and its Back returns there
 * (PLT-7159).
 */
function ChipLink({ href, title, children }: { href: string; title: string; children: ReactNode }) {
  const className = 'upright-chat-context-chip-link';
  if (href.startsWith('/') && !href.startsWith('//')) {
    return (
      <Link className={className} to={href} title={title}>
        {children}
      </Link>
    );
  }
  return (
    <a className={className} href={href} title={title}>
      {children}
    </a>
  );
}
